/**
 * Events Card - Virtual Host Events Module
 *
 * Allows restaurant owners to manage AI-powered event inquiries.
 * Upload event calendars via Excel, configure settings, view inquiries.
 *
 * Features:
 * - Enable/disable events module toggle
 * - Excel upload with template download
 * - Events list view with edit/delete
 * - Contact email settings
 * - Recent inquiries view
 *
 * Author: Dine AI
 * Created: December 7, 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Upload,
  Download,
  Loader,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Settings,
  Mail,
  Phone,
  Users,
  Clock,
  Trash2,
  Copy,
  Edit,
  Plus,
  FileSpreadsheet,
  AlertCircle,
  Eye,
  Music,
  DollarSign,
  Maximize2,
  Minimize2,
  Grid3X3,
  CalendarDays,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/services/api/apiClient';

// Helper for fetch with credentials (supports both OAuth and VH Staff httpOnly cookies)
const fetchWithCredentials = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(`/api${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = new Error('API request failed');
    (error as any).response = { status: response.status };
    throw error;
  }
  return response.json();
};

// ==================== Types ====================

interface EventItem {
  event_id: number;
  event_date: string;
  day_of_week: string;
  event_name: string;
  description: string | null;
  start_time: string | null;
  end_time: string | null;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  performer_name: string | null;
  has_cover_charge: boolean;
  cover_charge_amount: number | null;
  is_private: boolean;
  facebook_url: string | null;
  eventbrite_url: string | null;
  is_active: boolean;
}

interface EventsSettings {
  events_enabled: boolean;
  events_contact_emails: string[] | null;
  events_contact_name: string | null;
  events_contact_phone: string | null;
  venue_capacity: number | null;
  private_events_available: boolean;
  min_guests_private: number | null;
  max_guests_private: number | null;
  events_greeting_message: string | null;
  events_hume_config_id: string | null;
}

interface EventInquiry {
  inquiry_id: number;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  inquiry_type: string;
  inquiry_details: {
    event_type?: string;
    guest_count?: number;
    preferred_date?: string;
    budget?: string;
    notes?: string;
  } | null;
  follow_up_status: string;
  created_at: string;
}

interface UploadPreview {
  success: boolean;
  events_created: number;
  events_updated: number;
  errors: string[];
  preview: Array<{
    event_id: number;
    event_date: string;
    day_of_week: string;
    event_name: string;
    start_time: string;
    performer: string | null;
    has_cover_charge: boolean;
  }>;
}

// ==================== Component ====================

interface EventsCardProps {
  className?: string;
}

const EventsCard: React.FC<EventsCardProps> = ({ className = '' }) => {
  // Initialize state from URL params (for persistence on refresh)
  const getInitialFullScreen = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('events_view') === 'fullscreen';
    }
    return false;
  };

  const getInitialTab = (): 'events' | 'settings' | 'inquiries' => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('events_tab');
      if (tab === 'settings' || tab === 'inquiries') return tab;
    }
    return 'events';
  };

  // State
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<EventsSettings | null>(null);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [inquiries, setInquiries] = useState<EventInquiry[]>([]);
  // isExpanded state removed - card mode always shows preview
  const [activeTab, setActiveTab] = useState<'events' | 'settings' | 'inquiries'>(getInitialTab());
  const [includePastEvents, setIncludePastEvents] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(getInitialFullScreen());
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>('grid');

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<UploadPreview | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Settings form state
  const [contactEmails, setContactEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string>('');
  const [venueCapacity, setVenueCapacity] = useState<string>('');
  const [privateEventsAvailable, setPrivateEventsAvailable] = useState(true);
  const [minGuests, setMinGuests] = useState<string>('');
  const [maxGuests, setMaxGuests] = useState<string>('');
  const [greetingMessage, setGreetingMessage] = useState<string>('');

  // Event modal state (for both Add and Edit)
  const [editingEvent, setEditingEvent] = useState<EventItem | null>(null);
  const [isAddingEvent, setIsAddingEvent] = useState(false);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    type: 'event' | 'inquiry';
    id: number | null;
    name: string;
  }>({ isOpen: false, type: 'event', id: null, name: '' });

  const [editForm, setEditForm] = useState({
    event_name: '',
    event_date: '',
    start_time: '',
    end_time: '',
    performer_name: '',
    description: '',
    has_cover_charge: false,
    cover_charge_amount: '',
    is_private: false,
    facebook_url: '',
    eventbrite_url: '',
    is_active: true
  });

  // ==================== Data Fetching ====================

  const fetchSettings = async () => {
    try {
      const data = await fetchWithCredentials('/virtual-host/events/event-settings');
      setSettings(data);

      // Populate form fields
      setContactEmails(data.events_contact_emails || []);
      setContactName(data.events_contact_name || '');
      setContactPhone(formatPhoneNumber(data.events_contact_phone || ''));
      setVenueCapacity(data.venue_capacity?.toString() || '');
      setPrivateEventsAvailable(data.private_events_available !== false);
      setMinGuests(data.min_guests_private?.toString() || '');
      setMaxGuests(data.max_guests_private?.toString() || '');
      setGreetingMessage(data.events_greeting_message || '');
    } catch (error: any) {
      // 404 means not configured yet - that's OK
      if (error.response?.status !== 404) {
        console.error('Error fetching events settings:', error);
      }
      setSettings(null);
    }
  };

  const fetchEvents = async (includePast: boolean = false) => {
    try {
      const data = await fetchWithCredentials(`/virtual-host/events?include_past=${includePast}`);
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const fetchInquiries = async () => {
    try {
      const data = await fetchWithCredentials('/virtual-host/events/inquiries?limit=50');
      setInquiries(data.inquiries || []);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      setInquiries([]);
    }
  };

  const updateInquiryStatus = async (inquiryId: number, status: string) => {
    try {
      await fetchWithCredentials(`/virtual-host/events/inquiries/${inquiryId}/status?status=${status}`, { method: 'PUT' });
      toast.success(`Inquiry marked as ${status}`);
      await fetchInquiries();
    } catch (error) {
      console.error('Error updating inquiry:', error);
      toast.error('Failed to update inquiry');
    }
  };

  const openDeleteInquiryModal = (inquiry: EventInquiry) => {
    setDeleteModal({
      isOpen: true,
      type: 'inquiry',
      id: inquiry.inquiry_id,
      name: inquiry.customer_name || 'this inquiry'
    });
  };

  const deleteInquiry = async (inquiryId: number) => {
    try {
      await fetchWithCredentials(`/virtual-host/events/inquiries/${inquiryId}`, { method: 'DELETE' });
      toast.success('Inquiry deleted');
      await fetchInquiries();
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      toast.error('Failed to delete inquiry');
    }
  };

  // Helper to format relative time
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchSettings(), fetchEvents(includePastEvents), fetchInquiries()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Refetch events when includePastEvents changes
  useEffect(() => {
    fetchEvents(includePastEvents);
  }, [includePastEvents]);

  // Sync fullscreen and tab state to URL params (for persistence on refresh)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Handle fullscreen param
    if (isFullScreen) {
      params.set('events_view', 'fullscreen');
    } else {
      params.delete('events_view');
    }

    // Handle tab param
    if (activeTab !== 'events') {
      params.set('events_tab', activeTab);
    } else {
      params.delete('events_tab');
    }

    // Update URL without page reload
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [isFullScreen, activeTab]);

  // ==================== Toggle Events Module ====================

  const handleToggleEvents = async () => {
    const newEnabled = !settings?.events_enabled;

    // If enabling for the first time, need to create settings
    if (!settings && newEnabled) {
      setSaving(true);
      try {
        await fetchWithCredentials('/virtual-host/events/event-settings', {
          method: 'PUT',
          body: JSON.stringify({
            events_enabled: true,
            private_events_available: true
          })
        });
        await fetchSettings();
        toast.success('Events module enabled!');
      } catch (error) {
        console.error('Error enabling events:', error);
        toast.error('Failed to enable events module');
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    try {
      await fetchWithCredentials('/virtual-host/events/event-settings', {
        method: 'PUT',
        body: JSON.stringify({ events_enabled: newEnabled })
      });
      setSettings(prev => prev ? { ...prev, events_enabled: newEnabled } : null);
      toast.success(newEnabled ? 'Events module enabled!' : 'Events module disabled');
    } catch (error) {
      console.error('Error toggling events:', error);
      toast.error('Failed to update events setting');
    } finally {
      setSaving(false);
    }
  };

  // ==================== Excel Upload ====================

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch('/api/virtual-host/events/template', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to download template');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'events_template.xlsx';
      a.click();
      URL.revokeObjectURL(url);

      toast.success('Template downloaded!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel file (.xlsx, .xls) or CSV');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/virtual-host/events/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData  // Don't set Content-Type for FormData - browser sets it with boundary
      });
      if (!response.ok) throw new Error('Failed to upload file');
      const data = await response.json() as UploadPreview;

      if (data.events_created > 0) {
        toast.success(`${data.events_created} events uploaded successfully!`);
        await fetchEvents();
      }

      if (data.errors && data.errors.length > 0) {
        toast.error(`${data.errors.length} rows had errors`);
        // Show errors in modal
        setUploadPreview(data);
        setShowUploadModal(true);
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // ==================== Settings Save ====================

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Strip phone formatting before saving (store raw digits)
      const rawPhone = contactPhone.replace(/\D/g, '');

      await fetchWithCredentials('/virtual-host/events/event-settings', {
        method: 'PUT',
        body: JSON.stringify({
          events_contact_emails: contactEmails.length > 0 ? contactEmails : null,
          events_contact_name: contactName || null,
          events_contact_phone: rawPhone || null,
          venue_capacity: venueCapacity ? parseInt(venueCapacity) : null,
          private_events_available: privateEventsAvailable,
          min_guests_private: minGuests ? parseInt(minGuests) : null,
          max_guests_private: maxGuests ? parseInt(maxGuests) : null,
          events_greeting_message: greetingMessage || null
        })
      });

      toast.success('Settings saved!');
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // ==================== Event Actions ====================

  const openDeleteEventModal = (event: EventItem) => {
    setDeleteModal({
      isOpen: true,
      type: 'event',
      id: event.event_id,
      name: event.event_name
    });
  };

  const handleDeleteEvent = async (eventId: number) => {
    try {
      await fetchWithCredentials(`/virtual-host/events/${eventId}`, { method: 'DELETE' });
      toast.success('Event deleted');
      await fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal.id) return;

    if (deleteModal.type === 'event') {
      await handleDeleteEvent(deleteModal.id);
    } else {
      await deleteInquiry(deleteModal.id);
    }

    setDeleteModal({ isOpen: false, type: 'event', id: null, name: '' });
  };

  const handleToggleEventActive = async (event: EventItem) => {
    try {
      await fetchWithCredentials(`/virtual-host/events/${event.event_id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !event.is_active })
      });
      toast.success(event.is_active ? 'Event hidden from AI' : 'Event visible to AI');
      await fetchEvents(includePastEvents);
    } catch (error) {
      console.error('Error toggling event:', error);
      toast.error('Failed to update event');
    }
  };

  // ==================== Edit Event ====================

  const openEditModal = (event: EventItem) => {
    setEditingEvent(event);
    setIsAddingEvent(false);
    setEditForm({
      event_name: event.event_name,
      event_date: event.event_date,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      performer_name: event.performer_name || '',
      description: event.description || '',
      has_cover_charge: event.has_cover_charge,
      cover_charge_amount: event.cover_charge_amount?.toString() || '',
      is_private: event.is_private,
      facebook_url: event.facebook_url || '',
      eventbrite_url: event.eventbrite_url || '',
      is_active: event.is_active
    });
  };

  const openAddModal = () => {
    setEditingEvent(null);
    setIsAddingEvent(true);
    // Get tomorrow's date as default
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultDate = tomorrow.toISOString().split('T')[0];

    setEditForm({
      event_name: '',
      event_date: defaultDate,
      start_time: '',
      end_time: '',
      performer_name: '',
      description: '',
      has_cover_charge: false,
      cover_charge_amount: '',
      is_private: false,
      facebook_url: '',
      eventbrite_url: '',
      is_active: true
    });
  };

  const closeEventModal = () => {
    setEditingEvent(null);
    setIsAddingEvent(false);
  };

  const handleSaveEvent = async () => {
    setSaving(true);

    const eventData = {
      event_name: editForm.event_name,
      event_date: editForm.event_date,
      start_time: editForm.start_time || null,
      end_time: editForm.end_time || null,
      performer_name: editForm.performer_name || null,
      description: editForm.description || null,
      has_cover_charge: editForm.has_cover_charge,
      cover_charge_amount: editForm.cover_charge_amount ? parseFloat(editForm.cover_charge_amount) : null,
      is_private: editForm.is_private,
      facebook_url: editForm.facebook_url || null,
      eventbrite_url: editForm.eventbrite_url || null,
      is_active: editForm.is_active
    };

    try {
      if (isAddingEvent) {
        // Create new event
        await fetchWithCredentials('/virtual-host/events', {
          method: 'POST',
          body: JSON.stringify(eventData)
        });
        toast.success('Event created!');
      } else if (editingEvent) {
        // Update existing event
        await fetchWithCredentials(`/virtual-host/events/${editingEvent.event_id}`, {
          method: 'PUT',
          body: JSON.stringify(eventData)
        });
        toast.success('Event updated!');
      }

      closeEventModal();
      await fetchEvents(includePastEvents);
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(isAddingEvent ? 'Failed to create event' : 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  // ==================== Render Helpers ====================

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00'); // Treat as local date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const isPastEvent = (dateStr: string) => {
    // Extract just the date part (YYYY-MM-DD) in case the string has time component
    const datePart = dateStr.split('T')[0];
    // Create date at end of day in local timezone
    const eventDate = new Date(datePart + 'T23:59:59');
    const today = new Date();
    // Only compare dates, not times - event is past if event day < today's date
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
    return eventDateOnly < todayDateOnly;
  };

  // ==================== Email & Phone Helpers ====================

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    } else if (e.key === 'Backspace' && emailInput === '' && contactEmails.length > 0) {
      // Remove last email when backspace is pressed on empty input
      setContactEmails(contactEmails.slice(0, -1));
    }
  };

  const addEmail = () => {
    const email = emailInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !contactEmails.includes(email)) {
      setContactEmails([...contactEmails, email]);
      setEmailInput('');
    } else if (email && !isValidEmail(email)) {
      toast.error('Please enter a valid email address');
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setContactEmails(contactEmails.filter(email => email !== emailToRemove));
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setContactPhone(formatted);
  };

  // ==================== Calendar Helpers ====================

  const getCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday

    const days: (number | null)[] = [];

    // Add empty slots for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const formatDateKey = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getEventsForDate = (dateKey: string) => {
    return events.filter(event => {
      const eventDatePart = event.event_date.split('T')[0];
      return eventDatePart === dateKey;
    });
  };

  const getEventsForMonth = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-31`;

    return events.filter(event => {
      const eventDatePart = event.event_date.split('T')[0];
      return eventDatePart >= monthStart && eventDatePart <= monthEnd;
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCalendarMonth(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
    setSelectedDate(null);
  };

  const isToday = (year: number, month: number, day: number) => {
    const today = new Date();
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  };

  // ==================== Loading State ====================

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Event Manager</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  const isEnabled = settings?.events_enabled || false;
  const eventCount = events.filter(e => e.is_active).length;
  const inquiryCount = inquiries.filter(i => i.follow_up_status === 'pending').length;

  // Full screen container class
  const containerClass = isFullScreen
    ? 'fixed inset-0 z-50 bg-gray-100 overflow-auto'
    : `bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden ${className}`;

  // ==================== Main Render ====================

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${containerClass} group/events`}
    >
      {/* Header - Always Visible */}
      <div
        className={`p-6 ${isFullScreen ? 'sticky top-0 bg-white z-10 border-b border-gray-200 shadow-sm' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 group-hover/events:bg-purple-100 rounded-lg transition-colors">
              <Calendar className="h-5 w-5 text-gray-400 group-hover/events:text-purple-600 transition-colors" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Event Manager</h3>
              <p className="text-gray-500 text-sm">
                {isEnabled
                  ? `${eventCount} active events • ${inquiryCount} pending inquiries`
                  : 'AI-powered event inquiries'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Enable/Disable Toggle */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleToggleEvents();
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-all ${
                isEnabled
                  ? 'bg-green-100 border border-green-300'
                  : 'bg-gray-200 border border-gray-300'
              }`}
            >
              {saving ? (
                <Loader className="h-4 w-4 animate-spin text-gray-500" />
              ) : isEnabled ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-gray-500" />
              )}
              <span className={`text-sm font-medium ${isEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>

            {/* Full Screen Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFullScreen(!isFullScreen);
              }}
              className={`p-2 rounded-lg transition-colors ${
                isFullScreen
                  ? 'bg-[#FDB913] text-black hover:bg-[#e5a711]'
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
              title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
            >
              {isFullScreen ? (
                <Minimize2 className="h-5 w-5" />
              ) : (
                <Maximize2 className="h-5 w-5" />
              )}
            </button>

            {/* Close Button (Full Screen only) */}
            {isFullScreen && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFullScreen(false);
                }}
                className="p-2 bg-red-100 hover:bg-red-200 text-red-500 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}

          </div>
        </div>
      </div>

      {/* Content - Always visible */}
      <AnimatePresence>
        {true && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Card Mode: Compact Preview */}
            {!isFullScreen && (
              <div className="border-t border-gray-200 p-4">
                {/* Upcoming Events Preview */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-500 text-sm">Upcoming Events</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={openAddModal}
                      className="text-gray-500 text-sm hover:text-gray-700 flex items-center gap-1 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                    <button
                      onClick={() => setIsFullScreen(true)}
                      className="text-[#FDB913] text-sm hover:underline flex items-center gap-1"
                    >
                      View All
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {events.length === 0 ? (
                  <div className="text-center py-4">
                    <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm mb-1">No events yet</p>
                    <p className="text-gray-400 text-xs mb-3">Add your first event to get started</p>
                    <button
                      onClick={openAddModal}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FDB913] hover:bg-[#e5a711] text-black text-sm font-medium rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Add Event
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {events.slice(0, 3).map((event) => (
                      <div
                        key={event.event_id}
                        onClick={() => openEditModal(event)}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-[#FDB913]/50 border border-transparent transition-all"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 text-sm font-medium truncate">{event.event_name}</p>
                          <p className="text-gray-400 text-xs">
                            {formatDate(event.event_date)} • {event.start_time || 'TBD'}
                          </p>
                        </div>
                        {event.has_cover_charge && (
                          <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded flex items-center gap-0.5">
                            <DollarSign className="h-2.5 w-2.5" />
                            Cover
                          </span>
                        )}
                      </div>
                    ))}
                    {events.length > 3 && (
                      <p className="text-gray-400 text-xs text-center">+{events.length - 3} more events</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Full Screen Mode: Full Layout with Sidebar */}
            {isFullScreen && (
            <div className="border-t border-gray-200 flex p-3">
              {/* Vertical Icon Tabs */}
              <div className="flex flex-col gap-1 p-2">
                {/* Events Tab */}
                <button
                  onClick={() => setActiveTab('events')}
                  className={`relative p-3 rounded-lg transition-all group ${
                    activeTab === 'events'
                      ? 'bg-[#FDB913] text-black'
                      : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Events"
                >
                  <CalendarDays className="h-5 w-5" />
                  {events.length > 0 && (
                    <span className={`absolute -top-1 -right-1 text-xs font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ${
                      activeTab === 'events' ? 'bg-black text-[#FDB913]' : 'bg-[#FDB913] text-black'
                    }`}>
                      {events.length}
                    </span>
                  )}
                  {/* Tooltip */}
                  <span className="absolute left-full ml-2 px-2 py-1 bg-black/90 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                    Events
                  </span>
                </button>

                {/* Settings Tab */}
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`relative p-3 rounded-lg transition-all group ${
                    activeTab === 'settings'
                      ? 'bg-[#FDB913] text-black'
                      : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Settings"
                >
                  <Settings className="h-5 w-5" />
                  {/* Tooltip */}
                  <span className="absolute left-full ml-2 px-2 py-1 bg-black/90 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                    Settings
                  </span>
                </button>

                {/* Inquiries Tab */}
                <button
                  onClick={() => setActiveTab('inquiries')}
                  className={`relative p-3 rounded-lg transition-all group ${
                    activeTab === 'inquiries'
                      ? 'bg-[#FDB913] text-black'
                      : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  title="Inquiries"
                >
                  <MessageSquare className="h-5 w-5" />
                  {inquiryCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                      {inquiryCount}
                    </span>
                  )}
                  {/* Tooltip */}
                  <span className="absolute left-full ml-2 px-2 py-1 bg-black/90 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                    Inquiries
                  </span>
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 pl-6 pr-3 py-3">
                {/* Events Tab */}
                {activeTab === 'events' && (
                  <div>
                    {/* Upload Actions */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleDownloadTemplate}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium transition-colors"
                        >
                          <Download className="h-4 w-4" />
                          Download Template
                        </button>
                        <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium cursor-pointer transition-colors">
                          {uploading ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                          {uploading ? 'Processing...' : 'Upload Excel'}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                        </label>
                        <button
                          onClick={() => fetchEvents(includePastEvents)}
                          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                          title="Refresh events"
                        >
                          <RefreshCw className="h-4 w-4 text-gray-600" />
                        </button>
                        <button
                          onClick={openAddModal}
                          className="flex items-center gap-2 px-4 py-2 bg-[#FDB913] hover:bg-[#e5a711] rounded-lg text-black text-sm font-medium transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Add Event
                        </button>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* View Toggle */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded transition-colors ${
                              viewMode === 'grid' ? 'bg-[#FDB913] text-black' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Grid View"
                          >
                            <Grid3X3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setViewMode('calendar')}
                            className={`p-1.5 rounded transition-colors ${
                              viewMode === 'calendar' ? 'bg-[#FDB913] text-black' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            title="Calendar View"
                          >
                            <CalendarDays className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Include Past Events Toggle */}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includePastEvents}
                            onChange={(e) => setIncludePastEvents(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 bg-gray-50 text-[#FDB913] focus:ring-[#FDB913]"
                          />
                          <span className="text-gray-500 text-sm">Include past events</span>
                        </label>
                      </div>
                    </div>

                    {/* Grid View */}
                    {viewMode === 'grid' && (
                      events.length === 0 ? (
                      <div className="text-center py-8">
                        <FileSpreadsheet className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 mb-2">No events uploaded yet</p>
                        <p className="text-gray-400 text-sm">
                          Download the template and upload your event calendar
                        </p>
                      </div>
                    ) : (
                      <div className={`grid grid-cols-1 md:grid-cols-2 ${isFullScreen ? 'lg:grid-cols-3 xl:grid-cols-4' : ''} gap-3 ${isFullScreen ? '' : 'max-h-80'} overflow-y-auto`}>
                        {events.map((event) => {
                          const isPast = isPastEvent(event.event_date);
                          const isInactive = !event.is_active;
                          const isDimmed = isPast || isInactive;

                          return (
                            <div
                              key={event.event_id}
                              onClick={() => openEditModal(event)}
                              className={`p-3 rounded-lg border transition-all cursor-pointer ${
                                isDimmed
                                  ? 'bg-gray-100 border-gray-200 opacity-50'
                                  : 'bg-gray-50 border-gray-200 hover:border-[#FDB913]/50 hover:bg-gray-100'
                              }`}
                            >
                              {/* Header with title and actions */}
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h4 className={`font-medium text-sm leading-tight line-clamp-1 ${isDimmed ? 'text-gray-400' : 'text-gray-900'}`}>
                                  {event.event_name}
                                </h4>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleToggleEventActive(event); }}
                                    className={`p-1 rounded transition-colors ${
                                      event.is_active
                                        ? 'text-green-400 hover:bg-green-500/20'
                                        : 'text-gray-400 hover:bg-gray-500/20'
                                    }`}
                                    title={event.is_active ? 'Hide from AI' : 'Show to AI'}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openDeleteEventModal(event); }}
                                    className="p-1 text-red-400 hover:bg-red-100 rounded transition-colors"
                                    title="Delete event"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>

                              {/* Badges */}
                              <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                                {isPast && (
                                  <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-xs rounded">
                                    Past
                                  </span>
                                )}
                                {event.has_cover_charge && (
                                  <span className={`px-1.5 py-0.5 text-xs rounded flex items-center gap-0.5 ${isDimmed ? 'bg-yellow-50 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                                    <DollarSign className="h-2.5 w-2.5" />
                                    Cover
                                  </span>
                                )}
                                {event.is_recurring && (
                                  <span className={`px-1.5 py-0.5 text-xs rounded ${isDimmed ? 'bg-blue-500/10 text-blue-400/50' : 'bg-blue-100 text-blue-700'}`}>
                                    Recurring
                                  </span>
                                )}
                              </div>

                              {/* Date & Time */}
                              <div className={`text-xs space-y-1 ${isDimmed ? 'text-gray-400' : 'text-gray-500'}`}>
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3 flex-shrink-0" />
                                  <span>{formatDate(event.event_date)} ({event.day_of_week})</span>
                                  {event.start_time && (
                                    <span className={isDimmed ? 'text-gray-300' : 'text-gray-400'}>• {event.start_time}{event.end_time && ` - ${event.end_time}`}</span>
                                  )}
                                </div>
                                {event.performer_name && (
                                  <div className="flex items-center gap-1">
                                    <Music className="h-3 w-3 flex-shrink-0" />
                                    <span className="line-clamp-1">{event.performer_name}</span>
                                  </div>
                                )}
                                {event.description && (
                                  <p className={`line-clamp-2 mt-1 ${isDimmed ? 'text-gray-300' : 'text-gray-400'}`}>
                                    {event.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}

                    {/* Calendar View */}
                    {viewMode === 'calendar' && (
                      <div className={`flex gap-6 ${isFullScreen ? '' : 'flex-col'}`}>
                        {/* Calendar Section */}
                        <div className={`${isFullScreen ? 'flex-1' : 'w-full'}`}>
                          {/* Calendar Header */}
                          <div className="flex items-center justify-between mb-4">
                            <button
                              onClick={() => navigateMonth('prev')}
                              className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <ChevronLeft className="h-4 w-4 text-gray-600" />
                            </button>
                            <h3 className="text-gray-900 font-semibold">
                              {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </h3>
                            <button
                              onClick={() => navigateMonth('next')}
                              className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              <ChevronRight className="h-4 w-4 text-gray-600" />
                            </button>
                          </div>

                          {/* Calendar Grid - Compact */}
                          <div className="grid grid-cols-7 gap-1">
                            {/* Day Headers */}
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                              <div key={day} className="text-center text-gray-400 text-xs font-medium py-1">
                                {day}
                              </div>
                            ))}

                            {/* Calendar Days */}
                            {getCalendarDays(calendarMonth).map((day, index) => {
                              if (day === null) {
                                return <div key={`empty-${index}`} className="h-12" />;
                              }

                              const dateKey = formatDateKey(
                                calendarMonth.getFullYear(),
                                calendarMonth.getMonth(),
                                day
                              );
                              const dayEvents = getEventsForDate(dateKey);
                              const hasEvents = dayEvents.length > 0;
                              const isTodayDate = isToday(
                                calendarMonth.getFullYear(),
                                calendarMonth.getMonth(),
                                day
                              );
                              const isSelected = selectedDate === dateKey;
                              const isPast = isPastEvent(dateKey);

                              return (
                                <button
                                  key={day}
                                  onClick={() => setSelectedDate(isSelected ? null : dateKey)}
                                  className={`h-12 rounded-lg flex flex-col items-center justify-center transition-all relative ${
                                    isSelected
                                      ? 'bg-[#FDB913] text-black'
                                      : isTodayDate
                                      ? 'bg-purple-100 text-purple-700 border border-purple-300'
                                      : hasEvents
                                      ? 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                                      : 'text-gray-400 hover:bg-gray-50'
                                  } ${isPast && !isSelected ? 'opacity-50' : ''}`}
                                >
                                  <span className={`text-sm font-medium ${isSelected ? 'text-black' : ''}`}>{day}</span>
                                  {hasEvents && (
                                    <div className="flex gap-0.5 mt-0.5">
                                      {dayEvents.slice(0, 3).map((_, i) => (
                                        <div
                                          key={i}
                                          className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-black/60' : 'bg-[#FDB913]'}`}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Events Side Panel (Full Screen) / Below (Normal) */}
                        <div className={`${isFullScreen ? 'w-80 flex-shrink-0' : 'w-full'}`}>
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 h-full">
                            {selectedDate ? (
                              <>
                                <h4 className="text-gray-900 font-medium mb-3 text-sm">
                                  {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </h4>
                                {getEventsForDate(selectedDate).length === 0 ? (
                                  <p className="text-gray-500 text-sm">No events scheduled</p>
                                ) : (
                                  <div className="space-y-2">
                                    {getEventsForDate(selectedDate).map(event => (
                                      <div
                                        key={event.event_id}
                                        onClick={() => openEditModal(event)}
                                        className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-[#FDB913]/50 cursor-pointer transition-all"
                                      >
                                        <h5 className="text-gray-900 font-medium text-sm">{event.event_name}</h5>
                                        <div className="text-gray-500 text-xs mt-1 space-y-1">
                                          {event.start_time && (
                                            <div className="flex items-center gap-1">
                                              <Clock className="h-3 w-3" />
                                              {event.start_time}{event.end_time && ` - ${event.end_time}`}
                                            </div>
                                          )}
                                          {event.performer_name && (
                                            <div className="flex items-center gap-1">
                                              <Music className="h-3 w-3" />
                                              {event.performer_name}
                                            </div>
                                          )}
                                        </div>
                                        {event.has_cover_charge && (
                                          <span className="mt-2 inline-flex px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded items-center gap-0.5">
                                            <DollarSign className="h-2.5 w-2.5" />
                                            Cover
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                                <Calendar className="h-8 w-8 text-gray-300 mb-2" />
                                <p className="text-gray-400 text-sm">Select a date to view events</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Settings Tab - Modern Web 3.0 Layout */}
                {activeTab === 'settings' && (
                  <div className="space-y-8 max-w-3xl mx-auto">

                    {/* Section 1: Contact Information */}
                    <div className="space-y-5">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span className="text-sm font-medium uppercase tracking-wider">Contact Information</span>
                      </div>

                      {/* Contact Emails - Tag Style */}
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">Contact Emails</label>
                        <div className="flex flex-wrap gap-2 p-4 bg-gray-50 border border-gray-200 rounded-xl min-h-[56px] focus-within:border-[#FDB913]/50 transition-all">
                          {contactEmails.map((email, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full"
                            >
                              {email}
                              <button
                                type="button"
                                onClick={() => removeEmail(email)}
                                className="hover:text-gray-700 transition-colors"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          ))}
                          <input
                            type="email"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyDown={handleEmailKeyDown}
                            onBlur={addEmail}
                            placeholder={contactEmails.length === 0 ? "Type email and press Enter" : "Add another..."}
                            className="flex-1 min-w-[180px] bg-transparent text-gray-900 placeholder:text-gray-400 text-base focus:outline-none"
                          />
                        </div>
                        <p className="text-gray-400 text-sm mt-2">Press Enter or comma to add multiple emails</p>
                      </div>

                      {/* Name & Phone Row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-2">Contact Name</label>
                          <div className="relative">
                            <Users className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                            <input
                              type="text"
                              value={contactName}
                              onChange={(e) => setContactName(e.target.value)}
                              placeholder="John Smith"
                              className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-2">Contact Phone</label>
                          <div className="relative">
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                            <input
                              type="tel"
                              value={contactPhone}
                              onChange={handlePhoneChange}
                              placeholder="(555) 123-4567"
                              maxLength={14}
                              className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200" />

                    {/* Section 2: Venue Details */}
                    <div className="space-y-5">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4" />
                        <span className="text-sm font-medium uppercase tracking-wider">Venue Details</span>
                      </div>

                      {/* Venue Capacity */}
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">Venue Capacity</label>
                        <div className="relative w-40">
                          <input
                            type="number"
                            value={venueCapacity}
                            onChange={(e) => setVenueCapacity(e.target.value)}
                            placeholder="100"
                            className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">guests</span>
                        </div>
                      </div>

                      {/* Private Events Card */}
                      <div className={`p-5 rounded-xl border transition-all ${
                        privateEventsAvailable
                          ? 'bg-green-500/10 border-green-500/30'
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${privateEventsAvailable ? 'bg-green-100' : 'bg-gray-100'}`}>
                              <Calendar className={`h-5 w-5 ${privateEventsAvailable ? 'text-green-400' : 'text-gray-400'}`} />
                            </div>
                            <div>
                              <p className="text-gray-900 font-medium">Private Events Available</p>
                              <p className="text-gray-500 text-sm">Accept private event bookings</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setPrivateEventsAvailable(!privateEventsAvailable)}
                            className={`w-14 h-8 rounded-full transition-all relative ${
                              privateEventsAvailable ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full bg-white shadow-lg absolute top-1 transition-all ${
                              privateEventsAvailable ? 'left-7' : 'left-1'
                            }`} />
                          </button>
                        </div>

                        {/* Guest Range - Animated */}
                        <AnimatePresence>
                          {privateEventsAvailable && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <label className="block text-gray-700 text-sm font-medium mb-3">Guest Range</label>
                                <div className="flex items-center gap-4">
                                  <div className="flex-1">
                                    <label className="block text-gray-400 text-xs mb-1">Minimum</label>
                                    <input
                                      type="number"
                                      value={minGuests}
                                      onChange={(e) => setMinGuests(e.target.value)}
                                      placeholder="10"
                                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                                    />
                                  </div>
                                  <span className="text-gray-300 mt-5">—</span>
                                  <div className="flex-1">
                                    <label className="block text-gray-400 text-xs mb-1">Maximum</label>
                                    <input
                                      type="number"
                                      value={maxGuests}
                                      onChange={(e) => setMaxGuests(e.target.value)}
                                      placeholder="50"
                                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                                    />
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200" />

                    {/* Section 3: AI Settings */}
                    <div className="space-y-5">
                      <div className="flex items-center gap-2 text-gray-600">
                        <MessageSquare className="h-4 w-4" />
                        <span className="text-sm font-medium uppercase tracking-wider">AI Host Settings</span>
                      </div>

                      {/* Greeting Message */}
                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-2">Custom Greeting</label>
                        <textarea
                          value={greetingMessage}
                          onChange={(e) => setGreetingMessage(e.target.value)}
                          rows={2}
                          placeholder="Hi, thanks for calling! I'm Sarah, how can I help you with events today?"
                          className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all resize-none"
                        />
                        <p className="text-gray-400 text-sm mt-2">This greeting will be used by the AI Host when answering event inquiries</p>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="pt-4">
                      <button
                        onClick={handleSaveSettings}
                        disabled={saving}
                        className="w-full px-6 py-4 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-xl font-semibold text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#FDB913]/20"
                      >
                        {saving ? (
                          <>
                            <Loader className="h-5 w-5 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Check className="h-5 w-5" />
                            Save Settings
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Inquiries Tab - Modern Compact Cards */}
                {activeTab === 'inquiries' && (
                  <div className="max-w-4xl mx-auto">
                    {inquiries.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="p-4 bg-gray-50 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <Mail className="h-8 w-8 text-gray-300" />
                        </div>
                        <p className="text-gray-600 font-medium">No inquiries yet</p>
                        <p className="text-gray-400 text-sm mt-1">
                          Customer inquiries from AI calls will appear here
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {inquiries.map((inquiry) => {
                          const statusColors: Record<string, { bg: string; text: string; border: string }> = {
                            pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
                            contacted: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
                            booked: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
                            declined: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' }
                          };
                          const colors = statusColors[inquiry.follow_up_status] || statusColors.pending;
                          const details = inquiry.inquiry_details || {};

                          return (
                            <div
                              key={inquiry.inquiry_id}
                              className={`p-4 rounded-xl border ${colors.bg} ${colors.border} transition-all hover:border-gray-300`}
                            >
                              {/* Row 1: Status, Name, Type, Time */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  {/* Status Badge */}
                                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${colors.bg} ${colors.text} border ${colors.border}`}>
                                    {inquiry.follow_up_status}
                                  </span>
                                  {/* Name */}
                                  <span className="text-gray-900 font-semibold text-lg">{inquiry.customer_name || 'Unknown'}</span>
                                  {/* Type Badge */}
                                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                                    inquiry.inquiry_type === 'private_booking'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {inquiry.inquiry_type === 'private_booking' ? 'Private Event' : 'General'}
                                  </span>
                                </div>
                                <span className="text-gray-400 text-sm">{formatTimeAgo(inquiry.created_at)}</span>
                              </div>

                              {/* Row 2: Key Details */}
                              <div className="flex items-center gap-6 text-sm text-gray-600 mb-3">
                                {/* Phone - Click to copy */}
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(inquiry.customer_phone || '');
                                    toast.success('Phone copied!');
                                  }}
                                  className="flex items-center gap-1.5 hover:text-[#FDB913] transition-colors group"
                                  title="Click to copy phone number"
                                >
                                  <Phone className="h-4 w-4" />
                                  {inquiry.customer_phone}
                                  <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </button>
                                {/* Guest Count */}
                                {details.guest_count && (
                                  <span className="flex items-center gap-1.5">
                                    <Users className="h-4 w-4" />
                                    {details.guest_count} guests
                                  </span>
                                )}
                                {/* Preferred Date */}
                                {details.preferred_date && (
                                  <span className="flex items-center gap-1.5">
                                    <Calendar className="h-4 w-4" />
                                    {details.preferred_date}
                                  </span>
                                )}
                                {/* Email if exists */}
                                {inquiry.customer_email && (
                                  <a
                                    href={`mailto:${inquiry.customer_email}`}
                                    className="flex items-center gap-1.5 hover:text-[#FDB913] transition-colors"
                                  >
                                    <Mail className="h-4 w-4" />
                                    {inquiry.customer_email}
                                  </a>
                                )}
                              </div>

                              {/* Row 3: Action Buttons */}
                              <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                                {/* Status Actions - Only show relevant ones */}
                                {inquiry.follow_up_status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'contacted')}
                                      className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition-colors"
                                    >
                                      Contacted
                                    </button>
                                    <button
                                      onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'booked')}
                                      className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors"
                                    >
                                      Booked
                                    </button>
                                    <button
                                      onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'declined')}
                                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                                    >
                                      Declined
                                    </button>
                                  </>
                                )}

                                {inquiry.follow_up_status === 'contacted' && (
                                  <>
                                    <button
                                      onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'booked')}
                                      className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors"
                                    >
                                      Booked
                                    </button>
                                    <button
                                      onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'declined')}
                                      className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                                    >
                                      Declined
                                    </button>
                                  </>
                                )}

                                {/* Spacer */}
                                <div className="flex-1" />

                                {/* Delete Button */}
                                <button
                                  onClick={() => openDeleteInquiryModal(inquiry)}
                                  className="p-1.5 hover:bg-red-100 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                                  title="Delete inquiry"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Results Modal (shows errors if any) */}
      {showUploadModal && uploadPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white border border-gray-200 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Upload Results
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadPreview(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Summary */}
            <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg">
              <p className="text-green-700">
                <Check className="h-4 w-4 inline mr-2" />
                {uploadPreview.events_created} events created successfully
              </p>
            </div>

            {/* Errors */}
            {uploadPreview.errors && uploadPreview.errors.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-700 font-medium text-sm mb-1">Errors ({uploadPreview.errors.length} rows)</p>
                    <ul className="text-yellow-600 text-xs space-y-1 max-h-40 overflow-auto">
                      {uploadPreview.errors.map((e, i) => (
                        <li key={i}>• {e}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => {
                setShowUploadModal(false);
                setUploadPreview(null);
              }}
              className="w-full px-4 py-3 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}

      {/* Add/Edit Event Modal - Portal to body for proper z-index */}
      {(editingEvent || isAddingEvent) && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white border border-gray-200 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-xl">
                    <Calendar className="h-6 w-6 text-[#FDB913]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{isAddingEvent ? 'Create New Event' : 'Edit Event'}</h3>
                    <p className="text-gray-500 text-sm mt-0.5">Fill in the details for your event</p>
                  </div>
                </div>
                <button
                  onClick={closeEventModal}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors group"
                >
                  <X className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="px-8 py-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-8">

              {/* Section 1: Basic Info */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 text-gray-600">
                  <Edit className="h-4 w-4" />
                  <span className="text-sm font-medium uppercase tracking-wider">Event Details</span>
                </div>

                {/* Event Name - Full Width, Prominent */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Event Name <span className="text-[#FDB913]">*</span></label>
                  <input
                    type="text"
                    value={editForm.event_name}
                    onChange={(e) => setEditForm({ ...editForm, event_name: e.target.value })}
                    placeholder="e.g., Friday Night Jazz"
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    placeholder="Tell your guests what to expect..."
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all resize-none"
                  />
                </div>

                {/* Performer */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Performer / DJ</label>
                  <div className="relative">
                    <Music className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300" />
                    <input
                      type="text"
                      value={editForm.performer_name}
                      onChange={(e) => setEditForm({ ...editForm, performer_name: e.target.value })}
                      placeholder="e.g., DJ Smoove, The Jazz Trio"
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Section 2: Schedule */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium uppercase tracking-wider">Schedule</span>
                </div>

                {/* Date - Full Width */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">Date <span className="text-[#FDB913]">*</span></label>
                  <input
                    type="date"
                    value={editForm.event_date}
                    onChange={(e) => setEditForm({ ...editForm, event_date: e.target.value })}
                    className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                  />
                </div>

                {/* Time Row - Two Columns */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Start Time</label>
                    <input
                      type="text"
                      value={editForm.start_time}
                      onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                      placeholder="e.g., 7:00 PM"
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">End Time</label>
                    <input
                      type="text"
                      value={editForm.end_time}
                      onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                      placeholder="e.g., 11:00 PM"
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Section 3: Pricing */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 text-gray-600">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-sm font-medium uppercase tracking-wider">Pricing</span>
                </div>

                {/* Cover Charge Card */}
                <div className={`p-5 rounded-xl border transition-all ${
                  editForm.has_cover_charge
                    ? 'bg-yellow-50 border-yellow-300'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${editForm.has_cover_charge ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                        <DollarSign className={`h-5 w-5 ${editForm.has_cover_charge ? 'text-[#FDB913]' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">Cover Charge</p>
                        <p className="text-gray-500 text-sm">Require admission fee for this event</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setEditForm({ ...editForm, has_cover_charge: !editForm.has_cover_charge })}
                      className={`w-14 h-8 rounded-full transition-all relative ${
                        editForm.has_cover_charge ? 'bg-[#FDB913]' : 'bg-gray-300'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full bg-white shadow-lg absolute top-1 transition-all ${
                        editForm.has_cover_charge ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>

                  {/* Amount Input - Animated */}
                  <AnimatePresence>
                    {editForm.has_cover_charge && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <label className="block text-gray-700 text-sm font-medium mb-2">Amount</label>
                          <div className="relative w-40">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-medium">$</span>
                            <input
                              type="number"
                              value={editForm.cover_charge_amount}
                              onChange={(e) => setEditForm({ ...editForm, cover_charge_amount: e.target.value })}
                              placeholder="0.00"
                              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 text-lg font-medium placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Section 4: Links */}
              <div className="space-y-5">
                <div className="flex items-center gap-2 text-gray-600">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  <span className="text-sm font-medium uppercase tracking-wider">Event Links</span>
                </div>

                {/* Full Width URL Fields */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Facebook Event URL</label>
                    <input
                      type="url"
                      value={editForm.facebook_url}
                      onChange={(e) => setEditForm({ ...editForm, facebook_url: e.target.value })}
                      placeholder="https://facebook.com/events/..."
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">Eventbrite URL</label>
                    <input
                      type="url"
                      value={editForm.eventbrite_url}
                      onChange={(e) => setEditForm({ ...editForm, eventbrite_url: e.target.value })}
                      placeholder="https://eventbrite.com/e/..."
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-base placeholder:text-gray-400 focus:outline-none focus:border-[#FDB913] focus:ring-1 focus:ring-[#FDB913] transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Section 5: Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Settings className="h-4 w-4" />
                  <span className="text-sm font-medium uppercase tracking-wider">Visibility Settings</span>
                </div>

                {/* Toggle Cards - Side by Side */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Private Event Toggle */}
                  <div className={`p-4 rounded-xl border transition-all ${
                    editForm.is_private
                      ? 'bg-purple-500/10 border-purple-500/30'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-gray-900 font-medium">Private Event</p>
                      <button
                        onClick={() => setEditForm({ ...editForm, is_private: !editForm.is_private })}
                        className={`w-12 h-7 rounded-full transition-all relative ${
                          editForm.is_private ? 'bg-purple-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow-lg absolute top-1 transition-all ${
                          editForm.is_private ? 'left-6' : 'left-1'
                        }`} />
                      </button>
                    </div>
                    <p className="text-gray-500 text-sm">Only for invited guests</p>
                  </div>

                  {/* Visible to AI Toggle */}
                  <div className={`p-4 rounded-xl border transition-all ${
                    editForm.is_active
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-gray-900 font-medium">Visible to AI</p>
                      <button
                        onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })}
                        className={`w-12 h-7 rounded-full transition-all relative ${
                          editForm.is_active ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow-lg absolute top-1 transition-all ${
                          editForm.is_active ? 'left-6' : 'left-1'
                        }`} />
                      </button>
                    </div>
                    <p className="text-gray-500 text-sm">AI Host can share this event</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer - Fixed at Bottom */}
            <div className="px-8 py-5 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-4">
                <button
                  onClick={closeEventModal}
                  className="flex-1 px-6 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEvent}
                  disabled={saving || !editForm.event_name || !editForm.event_date}
                  className="flex-1 px-6 py-3.5 bg-[#FDB913] hover:bg-[#e5a711] text-black rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#FDB913]/20"
                >
                  {saving ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      {isAddingEvent ? 'Creating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5" />
                      {isAddingEvent ? 'Create Event' : 'Save Changes'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal - Web 3.0 Styled */}
      {deleteModal.isOpen && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="bg-white border border-gray-200 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <Trash2 className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Delete {deleteModal.type === 'event' ? 'Event' : 'Inquiry'}
                  </h3>
                  <p className="text-gray-500 text-sm mt-0.5">This action cannot be undone</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6">
              <p className="text-gray-700 text-base">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-gray-900">"{deleteModal.name}"</span>?
              </p>
              {deleteModal.type === 'event' && (
                <p className="text-gray-500 text-sm mt-3">
                  This event will be permanently removed from your calendar and AI will no longer mention it.
                </p>
              )}
              {deleteModal.type === 'inquiry' && (
                <p className="text-gray-500 text-sm mt-3">
                  This inquiry and all associated contact information will be permanently deleted.
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, type: 'event', id: null, name: '' })}
                  className="flex-1 px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-5 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-500/20"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default EventsCard;
